import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BloomTracker from './BloomTracker';
import type { BloomUpdate } from '@hooks/useAnnaChat';
import type { TeachingForm } from '../../types';

interface SessionHeaderProps {
  subtopicTitle: string;
  teachingForm: TeachingForm;
  bloomUpdates: BloomUpdate[];
  accentColor: string;
  onEnd: () => void;
}

const BADGE_MAP: Record<string, string> = {
  amber: 'bg-amber-500/20 text-amber-300',
  blue: 'bg-blue-500/20 text-blue-300',
  indigo: 'bg-indigo-500/20 text-indigo-300',
};

export default function SessionHeader({
  subtopicTitle,
  teachingForm,
  bloomUpdates,
  accentColor,
  onEnd,
}: SessionHeaderProps) {
  const { t } = useTranslation('session');
  const [showConfirm, setShowConfirm] = useState(false);
  const badge = BADGE_MAP[accentColor] || 'bg-gray-700 text-gray-300';

  return (
    <header className="h-14 bg-black/40 backdrop-blur flex items-center px-4 gap-3 shrink-0">
      {/* Mode label */}
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${badge}`}>
        {t(`header.modeLabels.${teachingForm}`)}
      </span>

      {/* Topic title */}
      <span className="text-sm text-gray-300 truncate min-w-0 flex-1">
        {subtopicTitle}
      </span>

      {/* BloomTracker — hidden on mobile */}
      <div className="hidden md:flex">
        <BloomTracker bloomUpdates={bloomUpdates} />
      </div>

      {/* End session */}
      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors"
        >
          {t('header.endSession')}
        </button>
      ) : (
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gray-400 hidden sm:inline">
            {t('header.endConfirm')}
          </span>
          <button
            onClick={onEnd}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 hover:bg-red-500 text-white transition-colors"
          >
            {t('header.endConfirmYes')}
          </button>
          <button
            onClick={() => setShowConfirm(false)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors"
          >
            {t('header.endConfirmNo')}
          </button>
        </div>
      )}
    </header>
  );
}
